import React from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import seoPages from '../data/seoPages.json';
import { SITE_CONFIG } from '../config';
import { SEO_CONFIG } from '../config/seo';
import MarketingLayout from './MarketingLayout';
import SEOHelmet from './SEOHelmet';
import './Guides.css';
import { useConsultationModal } from '../contexts/ConsultationModalContext';

export default function GuidePage() {
  const { openConsultationModal } = useConsultationModal();
  const { slug } = useParams();
  const page = seoPages.find(item => item.slug === slug);
  if (!page) return <Navigate to="/guides" replace />;

  const path = `/guides/${page.slug}`;
  const related = page.relatedSlugs.map(relatedSlug => seoPages.find(item => item.slug === relatedSlug)).filter(Boolean);
  const schema = [
    {
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: page.h1,
      description: page.metaDescription,
      mainEntityOfPage: `${SEO_CONFIG.siteUrl}${path}`,
      author: { '@type': 'Organization', name: SITE_CONFIG.BUSINESS.NAME, url: SEO_CONFIG.siteUrl },
      publisher: { '@id': `${SEO_CONFIG.siteUrl}/#business` },
      about: page.category === 'Handyman guide' ? 'Home repair and handyman work in Maricopa, Arizona' : 'Home remodeling in Maricopa, Arizona'
    },
    {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: page.faqs.map(item => ({
        '@type': 'Question',
        name: item.question,
        acceptedAnswer: { '@type': 'Answer', text: item.answer }
      }))
    }
  ];

  return <MarketingLayout>
    <SEOHelmet
      title={page.title}
      description={page.metaDescription}
      path={path}
      type="article"
      schema={schema}
      breadcrumbs={[{ name: 'Home', path: '/' }, { name: 'Guides', path: '/guides' }, { name: page.h1, path }]}
    />
    <article>
      <header className="page-hero guide-article-hero"><div className="page-width">
        <p className="breadcrumbs"><Link to="/">Home</Link> / <Link to="/guides">Guides</Link> / {page.h1}</p>
        <p className="eyebrow eyebrow-light">{page.category} · Maricopa, Arizona</p>
        <h1 className="display-title">{page.h1}</h1>
        <p className="section-copy">{page.summary}</p>
      </div></header>

      <div className="page-width guide-article-layout">
        <aside className="guide-sidebar" aria-label="On this page">
          <p className="eyebrow">In this guide</p>
          <ol>{page.sections.map(section => <li key={section.heading}><a href={`#${section.heading.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')}`}>{section.heading}</a></li>)}</ol>
          <Link className="button button-small" to={page.servicePath}>{page.serviceLabel}</Link>
        </aside>
        <div className="guide-prose guide-article-copy">
          {page.sections.map(section => {
            const id = section.heading.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
            return <section id={id} key={section.heading}>
              <h2>{section.heading}</h2>
              {section.paragraphs.map(paragraph => <p key={paragraph}>{paragraph}</p>)}
            </section>;
          })}

          <section className="guide-checklist" aria-labelledby="project-checklist">
            <p className="eyebrow">Project checklist</p>
            <h2 id="project-checklist">Questions to settle before work starts</h2>
            <ul>{page.checklist.map(item => <li key={item}>{item}</li>)}</ul>
          </section>

          <section className="guide-faq" aria-labelledby="guide-faq-heading">
            <p className="eyebrow">Frequently asked</p>
            <h2 id="guide-faq-heading">Questions about {page.h1.toLowerCase()}</h2>
            <div className="faq-list">{page.faqs.map(item => <div className="faq-item" key={item.question}><details><summary>{item.question}</summary><p>{item.answer}</p></details></div>)}</div>
          </section>
        </div>
      </div>
    </article>

    <section className="content-section guide-related"><div className="page-width">
      <div className="section-head"><div><p className="eyebrow">Keep planning</p><h2 className="section-title">Related Maricopa guides.</h2></div><Link className="text-link" to="/guides">See all 20 guides</Link></div>
      <div className="guide-card-grid guide-card-grid-three">{related.map(item => <Link className="guide-card" to={`/guides/${item.slug}`} key={item.slug}><span className="eyebrow">{item.category}</span><h3>{item.h1}</h3><p>{item.summary}</p><span className="text-link">Read the guide</span></Link>)}</div>
    </div></section>

    <section className="cta-band"><div className="page-width"><h2>Turn the research into a clear project plan.</h2><button className="button button-light" type="button" onClick={() => openConsultationModal()}>Schedule a free consultation</button></div></section>
  </MarketingLayout>;
}
